import { useEffect, useState } from "react";
import usePartAssembly from "../../../../data/usePartAssembly";
import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart, IPartAssembly } from "../../../../models/Part";
import { PartCategoryConflictMap } from "../../../../models/PartCategorySubPart";
import LoadingWheel from "../../../shared/LoadingWheel";
import { IPartAssemblyPartCategories } from '../../../../models/Part';
import LeadPartsCategoriesTable from "./LeadPartsCategoriesTable";
import { IVehicle } from "../../../../models/Vehicle";
import { CheckFeatureFlag } from "../../../../hooks/useFeatureFlag";

export interface PartAssemblyWithMatchingCategories extends IPartAssembly {
  matchingCategories: CategoryWithParts[];
}

export interface LeadPartsCategoriesProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  handleTableSelectionChange: (tableId: string, update: CategoryWithParts[] | ((prev: CategoryWithParts[]) => CategoryWithParts[])) => void;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
  showBestPrice?: boolean;
  /** Global (cross-table) main/sub-part clashes, keyed by category id. */
  categoryConflicts?: PartCategoryConflictMap;
  /** Sub-part ids the TME auto-select must skip. `undefined` while the link data loads. */
  autoSelectSuppressedCategoryIds?: Set<string>;
};

export default function LeadPartsCategories({
  categories,
  selectedCategories,
  handleTableSelectionChange,
  partProUnlocked,
  partProLoading,
  vehicle,
  filter,
  leadPartsTableFilters,
  showBestPrice = false,
  categoryConflicts,
  autoSelectSuppressedCategoryIds,
}: LeadPartsCategoriesProps) {
  const { assemblies } = usePartAssembly();
  const hasTmeFeature = CheckFeatureFlag("TME");
  const [isLoading, setIsLoading] = useState(true);
  const [categoryAssemblies, setCategoryAssemblies] = useState<PartAssemblyWithMatchingCategories[] | undefined>([]);
  const [unmatchedCategories, setUnmatchedCategories] = useState<CategoryWithParts[] | undefined>([]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const getUniqueCategories = (incoming: CategoryWithParts[]) => {
    const byCategoryId = new Map<string, CategoryWithParts>();
    incoming.forEach((category) => {
      if (!byCategoryId.has(category.category.id)) {
        byCategoryId.set(category.category.id, category);
      }
    });
    return [...byCategoryId.values()];
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (!assemblies.isLoading && categories) {
      const uniqueCategories = getUniqueCategories(categories);

      //Find matching categories for each assembly and unmatched categories
      const matchedAssemblies: PartAssemblyWithMatchingCategories[] = assemblies.data?.data.map((assembly: IPartAssembly) => {
        // Create a map of categoryId to order for quick lookup
        const categoryOrderMap = new Map(
          assembly.partAssemblyPartCategories?.map((cat, index) => [cat.partCategoryId, index]) ?? []
        );

        // Filter and sort categories based on the order in partAssemblyPartCategories
        const matchingCategories: CategoryWithParts[] = uniqueCategories
          .filter(category =>
            assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) => cat.partCategoryId === category.category.id)
          )
          .sort((a, b) => {
            const orderA = categoryOrderMap.get(a.category.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = categoryOrderMap.get(b.category.id) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        return {
          ...assembly,
          matchingCategories,
        };
      }) ?? [];


      // Filter out categories that have no matching assemblies
      const unmatchedCategories = uniqueCategories.filter(category =>
        !matchedAssemblies?.some(assembly =>
          assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) => cat.partCategoryId === category.category.id)
        )
      );

      // console.log("Matched Assemblies", matchedAssemblies);
      // console.log("Unmatched Categories", unmatchedCategories);

      setCategoryAssemblies(matchedAssemblies);
      setUnmatchedCategories(unmatchedCategories);

      // setPartAssemblies(assemblies.data);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, categories]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div>

      {categoryAssemblies && categoryAssemblies.length > 0 && (
        categoryAssemblies.map((matchedAssembly: PartAssemblyWithMatchingCategories) => {

          const preselectedForThisAssembly = matchedAssembly.matchingCategories.filter(cat =>
            selectedCategories?.some(sel => sel.category.id === cat.category.id)
          );

          return (matchedAssembly.matchingCategories.length > 0 && (
            <div key={matchedAssembly.id} className="first:mt-0 mt-8">
              <h2 className="font-semibold">
                <button
                  type="button"
                  className="flex items-center gap-x-2 cursor-pointer hover:text-primary-600 select-none"
                  onClick={() => toggleSection(matchedAssembly.id)}
                >
                  <span
                    className={`transform transition-transform duration-200 ${collapsedSections.has(matchedAssembly.id) ? "rotate-0" : "rotate-90"
                      }`}
                  >
                    ▶
                  </span>
                  {matchedAssembly.name}
                </button>
              </h2>

              {!collapsedSections.has(matchedAssembly.id) && (
                <LeadPartsCategoriesTable
                  categories={matchedAssembly.matchingCategories}
                  selectedCategories={preselectedForThisAssembly}
                  setSelectedCategories={(update) => handleTableSelectionChange(matchedAssembly.id, update)}
                  partProUnlocked={partProUnlocked}
                  partProLoading={partProLoading}
                  vehicle={vehicle}
                  filter={filter}
                  leadPartsTableFilters={leadPartsTableFilters}
                  showBestPrice={showBestPrice}
                  autoSelect={hasTmeFeature}
                  categoryConflicts={categoryConflicts}
                  autoSelectSuppressedCategoryIds={autoSelectSuppressedCategoryIds}
                />
              )}
            </div>
          ))
        })
      )}

      {categoryAssemblies && unmatchedCategories && unmatchedCategories?.length > 0 && (() => {
        const preselectedForUnmatched = unmatchedCategories.filter(cat =>
          selectedCategories?.some(sel => sel.category.id === cat.category.id)
        );

        return (<div className="mt-8">
          <h2 className="font-semibold">
            <button
              type="button"
              className="flex items-center gap-x-2 cursor-pointer hover:text-primary-600 select-none"
              onClick={() => toggleSection("unmatched")}
            >
              <span
                className={`transform transition-transform duration-200 ${collapsedSections.has("unmatched") ? "rotate-0" : "rotate-90"
                  }`}
              >
                ▶
              </span>
              Non-Assembly Parts
            </button>
          </h2>
          {!collapsedSections.has("unmatched") && (
            <LeadPartsCategoriesTable
              categories={unmatchedCategories ?? []}
              selectedCategories={preselectedForUnmatched}
              setSelectedCategories={(update) => handleTableSelectionChange("unmatched", update)}
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
              vehicle={vehicle}
              filter={filter}
              leadPartsTableFilters={leadPartsTableFilters}
              showBestPrice={showBestPrice}
              categoryConflicts={categoryConflicts}
            />
          )}
        </div>
        )
      })()}
    </div>
  );
}